import { atom } from "jotai";
import { CellClickedEvent } from "ag-grid-community";
import {
    DeltaType,
    MetricType,
    PeriodOption,
    WithDeltaCalculator,
} from "@/features/Metrics/Metrics.types";
import {
    asOfDateAtom,
    metricsAtom,
} from "@/features/Metrics/state/Metrics.state";
import { MetricCodeWithFallbacks } from "@/features/Metrics/Metrics";
import {
    HydratedMetric,
    HydratedMetricObject,
} from "@/features/Metrics/HydratedMetric";
import { PeriodRange } from "@/features/Metrics/PeriodRange";
import { AIMContextViewAtom, AIMViewType } from "../../panels/AIMPanelContext";
import { Period } from "@/features/Metrics/Period";
import { EmptyMetricMessageType } from "../../panels/EmptyMetricPanel";
import { METRIC_CONFIG } from "@/features/Metrics/METRIC_CONFIG";

export const getPeriodsAtom = atom((get) => {
    const metrics = get(metricsAtom);
    return (
        codes: MetricCodeWithFallbacks[],
        selectedOption: PeriodOption,
    ): PeriodRange | null => {
        const asOfDate = get(asOfDateAtom);
        if (!metrics?.data) return null;
        return selectedOption.getPeriodRange(
            asOfDate,
            metrics.data?.getGlobalPeriodRange(
                selectedOption.resolution,
                codes,
            ) ?? null,
        );
    };
});

export const handleCellClickAtom = atom(
    null,
    (
        get,
        set,
        params: CellClickedEvent<WithDeltaCalculator<HydratedMetricObject>>,
        deltaType: DeltaType,
        selectedOption: PeriodOption,
    ) => {
        const field = params.colDef.field;

        if (field === "label" || !field || !params.data) return;

        const selectedCell = {
            rowId: params.node?.id ?? "",
            colField: field,
            gridId: params.api.getGridId(),
        };
        if (field === "delta") {
            if (
                params.data?.deltaCalculator &&
                params.data.deltaCalculator.metric.type !==
                    MetricType.PERCENTAGE
            ) {
                set(AIMContextViewAtom, {
                    type: AIMViewType.DELTA_LINEAGE,
                    props: {
                        delta: params.data.deltaCalculator.getDelta(
                            deltaType,
                            selectedOption,
                        ),
                    },
                });
            } else {
                console.log(METRIC_CONFIG[params.data.code]);
                set(AIMContextViewAtom, {
                    type: AIMViewType.EMPTY_METRIC,
                    props: {
                        config: METRIC_CONFIG[params.data.code],
                        messageType:
                            METRIC_CONFIG[params.data.code].type ===
                            MetricType.PERCENTAGE
                                ? EmptyMetricMessageType.LINEAGE_NOT_SUPPORTED
                                : EmptyMetricMessageType.NO_DATA,
                        panelTitle: `${METRIC_CONFIG[params.data.code].label} - % Change`,
                    },
                });
            }
            return;
        } else if (!params.data.rawData) {
            console.log(params.data.rawData);
            set(AIMContextViewAtom, {
                type: AIMViewType.EMPTY_METRIC,
                props: {
                    config: METRIC_CONFIG[params.data.code],
                    messageType: EmptyMetricMessageType.NO_DATA,
                },
            });
        } else {
            if (!params.data[field]) {
                set(AIMContextViewAtom, {
                    type: AIMViewType.EMPTY_METRIC,
                    props: {
                        config: METRIC_CONFIG[params.data.code],
                        messageType: EmptyMetricMessageType.NO_DATA,
                    },
                });
            } else {
                set(AIMContextViewAtom, {
                    type: AIMViewType.METRIC_LINEAGE,
                    props: {
                        metric: new HydratedMetric(
                            params.data.rawData,
                            selectedOption.resolution,
                        ),
                        period: Period.fromShortname(field),
                    },
                    isExpanded: true,
                    selectedCell,
                });
            }
        }
    },
);
